/**
 * A utility module to perform standardized week calculations based on a Monday-starting week.
 */

/**
 * Define the system's "epoch" - the Monday of the very first week we count.
 * This date is arbitrary but MUST NOT be changed once in use.
 * Let's use the first Monday of 2025.
 * This is Mon, Dec 30, 2024, which is the start of ISO week 1 for the year 2025.
 * ISO week 1 of any year is defined as the first week that contains at least 4 days of the new year,
 * and since January 1, 2025 falls on a Wednesday, that week (starting Monday, December 30, 2024)
 * contains 4 days of 2025 (Wednesday through Saturday), making it ISO week 1 of 2025.
 */
const EPOCH_MONDAY = new Date(2024, 11, 30); // Month is 0-indexed in JS/TS

/**
 * Information about a week
 */
export interface WeekInfo {
    readonly globalWeekNumber: number;
    readonly monday: Date;
    readonly year: number;
    readonly weekOfYear: number;
}

/**
 * Gets the Monday of the week for a given date.
 * @param date - The date to find the week for
 * @returns The date of the Monday of that week
 */
export function getMondayOfWeek(date: Date): Date {
    const result = new Date(date);
    const day = result.getDay();

    // getDay() returns 0 for Sunday, 1 for Monday, etc.
    // Calculate offset to get back to Monday
    let offset = day - 1; // Monday is 1
    if (offset < 0) {
        offset += 7; // Handle Sunday (0 - 1 = -1, so add 7)
    }

    result.setDate(result.getDate() - offset);
    result.setHours(0, 0, 0, 0); // Reset to start of day
    return result;
}

/**
 * Gets the end of the day on Sunday for the week of a given date.
 * @param date - The date to find the week for
 * @returns The date of the Sunday of that week, with the time set to 23:59:59.999
 */
export function getEndOfSundayOfWeek(date: Date): Date {
    const monday = getMondayOfWeek(date);
    const sunday = new Date(monday);
    sunday.setDate(sunday.getDate() + 6);
    sunday.setHours(23, 59, 59, 999);
    return sunday;
}

/**
 * Gets the ISO week number for a date.
 * Implementation based on ISO 8601 standard.
 * @param date - The date to get the week number for
 * @returns The ISO week number
 */
function getISOWeekNumber(date: Date): number {
    const tempDate = new Date(date.getTime());
    tempDate.setHours(0, 0, 0, 0);

    // Set to Thursday in current week (Thursday is the key day for ISO week)
    tempDate.setDate(tempDate.getDate() + 3 - ((tempDate.getDay() + 6) % 7));

    // Get first week of the year
    const firstWeek = new Date(tempDate.getFullYear(), 0, 4);

    // Calculate full weeks between first week and current date
    const diff = tempDate.getTime() - firstWeek.getTime();
    const oneWeek = 1000 * 60 * 60 * 24 * 7;

    return (
        1 +
        Math.round(
            diff / oneWeek +
                ((firstWeek.getDay() + 6) % 7) / 7 -
                ((tempDate.getDay() + 6) % 7) / 7
        )
    );
}

/**
 * Retrieves week information containing global week number and Monday's date
 * for any given date.
 * @param forDate - The date to get week information for
 * @returns A WeekInfo object
 * @throws Error if the date is before the system epoch
 */
export function getWeekInfo(forDate: Date): WeekInfo {
    const monday = getMondayOfWeek(forDate);

    if (monday < EPOCH_MONDAY) {
        throw new Error(
            `Date cannot be before the system epoch of ${EPOCH_MONDAY.toLocaleDateString()}`
        );
    }

    // Calculate the global week number
    const daysSinceEpoch = Math.floor(
        (monday.getTime() - EPOCH_MONDAY.getTime()) / (1000 * 60 * 60 * 24)
    );
    const globalWeekNumber = Math.floor(daysSinceEpoch / 7) + 1;

    // Get ISO week info for display purposes
    const weekOfYear = getISOWeekNumber(monday);

    return {
        globalWeekNumber,
        monday: new Date(monday), // Return a copy to prevent mutation
        year: monday.getFullYear(),
        weekOfYear
    };
}

/**
 * Gets the Monday date from a global week number.
 * @param globalWeekNumber - The global week number
 * @returns The date of the Monday for that global week
 * @throws Error if the global week number is less than 1
 */
export function getMondayFromGlobalWeek(globalWeekNumber: number): Date {
    if (globalWeekNumber < 1) {
        throw new Error('Global week number must be 1 or greater.');
    }

    const result = new Date(EPOCH_MONDAY);
    result.setDate(result.getDate() + (globalWeekNumber - 1) * 7);
    return result;
}

/**
 * Gets the date range (Monday to Sunday) for a given global week number.
 * @param globalWeekNumber - The global week number
 * @returns An object with startDate (Monday) and endDate (Sunday) for the week
 * @throws Error if the global week number is less than 1
 */
export function getWeekDateRange(globalWeekNumber: number): {
    startDate: Date;
    endDate: Date;
} {
    const monday = getMondayFromGlobalWeek(globalWeekNumber);
    const sunday = new Date(monday);
    sunday.setDate(sunday.getDate() + 6);
    sunday.setHours(23, 59, 59, 999);

    return {
        startDate: monday,
        endDate: sunday
    };
}

const MONTH_LABELS = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec'
];

/**
 * Formats a global week number as the ISO week label users see on the
 * timeline, plus the week's date range — e.g. "W43 2026 (19–25 Oct)" or
 * "W44 2026 (26 Oct – 1 Nov)" when the week spans two months.
 *
 * Use this anywhere a week number is shown to users; raw global week
 * numbers (weeks since the system epoch) are internal and confusing.
 * @param globalWeekNumber - The global week number
 * @returns The formatted label
 */
export function formatGlobalWeekLabel(globalWeekNumber: number): string {
    const {startDate, endDate} = getWeekDateRange(globalWeekNumber);
    const weekOfYear = getISOWeekNumber(startDate);
    const isoYear = getISOWeekYear(startDate);

    const range =
        startDate.getMonth() === endDate.getMonth()
            ? `${startDate.getDate()}–${endDate.getDate()} ${MONTH_LABELS[endDate.getMonth()]}`
            : `${startDate.getDate()} ${MONTH_LABELS[startDate.getMonth()]} – ${endDate.getDate()} ${MONTH_LABELS[endDate.getMonth()]}`;

    return `W${weekOfYear} ${isoYear} (${range})`;
}

/**
 * Formats a global week number as a compact ISO-week + Monday-date label —
 * e.g. "W28 Jul 13 2026" (W{isoWeek} {Mon} {day} {year}). Used for the AI
 * assistant page context, where a human-readable week beats a raw global week
 * number. See src/assistant/pageContext.ts.
 * @param globalWeekNumber - The global week number
 * @returns The formatted label
 */
export function formatWeekContextLabel(globalWeekNumber: number): string {
    const monday = getMondayFromGlobalWeek(globalWeekNumber);
    const weekOfYear = getISOWeekNumber(monday);
    return `W${weekOfYear} ${MONTH_LABELS[monday.getMonth()]} ${monday.getDate()} ${monday.getFullYear()}`;
}

/**
 * Adds a specified number of days to a date.
 * @param date - The starting date
 * @param days - The number of days to add (can be negative)
 * @returns A new Date object with the days added
 */
export function addDays(date: Date, days: number): Date {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
}

/**
 * Gets the ISO week-numbering year for a given date.
 * The ISO week-numbering year may differ from the calendar year
 * at the beginning or end of the year.
 * @param date - The date to get the ISO year for
 * @returns The ISO week-numbering year
 */
export function getISOWeekYear(date: Date): number {
    const tempDate = new Date(date.getTime());
    tempDate.setHours(0, 0, 0, 0);

    // Set to Thursday in current week (Thursday is the key day for ISO week)
    tempDate.setDate(tempDate.getDate() + 3 - ((tempDate.getDay() + 6) % 7));

    // Return the year of that Thursday
    return tempDate.getFullYear();
}

/**
 * Check if a date falls on a weekend (Saturday or Sunday)
 */
export function isWeekendDay(date: Date): boolean {
    const day = date.getDay();
    return day === 0 || day === 6; // 0 = Sunday, 6 = Saturday
}

/**
 * Check if a date range includes any weekend days
 */
export function includesWeekendDays(startDate: Date, endDate: Date): boolean {
    const start = new Date(startDate);
    start.setHours(0, 0, 0, 0);

    const end = new Date(endDate);
    end.setHours(23, 59, 59, 999);

    const current = new Date(start);
    while (current <= end) {
        if (isWeekendDay(current)) {
            return true;
        }
        current.setDate(current.getDate() + 1);
    }
    return false;
}

/**
 * Check if a date range contains ONLY weekend days
 */
export function isWeekendOnly(startDate: Date, endDate: Date): boolean {
    const start = new Date(startDate);
    start.setHours(0, 0, 0, 0);

    const end = new Date(endDate);
    end.setHours(23, 59, 59, 999);

    const current = new Date(start);
    while (current <= end) {
        if (!isWeekendDay(current)) {
            return false;
        }
        current.setDate(current.getDate() + 1);
    }
    return true;
}
